/**
 * Zod schema for the `travelItems` collection.
 *
 * Per-event travel records (flights, lodging, ground transport, catchall)
 * targeted via a single `audienceId` reference (ADR-120). The Audience
 * can be a custom group, a role audience, a personal audience (most
 * common for individual flights/lodging), or an anonymous composition.
 * Optional reminder fires a notification via the audience-aware
 * sendNotification pipeline a configurable lead time before the relevant
 * travel timestamp.
 *
 * Single-doc CRUD via service layer (ADR-018) — no callable.
 *
 * Read access uses
 * `request.auth.token.personId in resource.data.targetedPersonIds`
 * (ADR-137) — single array-contains check. The `targetedPersonIds[]`
 * cache is copied from the linked audience's `resolvedPersonIds[]` by
 * `onTravelItemWritten` and the cascading triggers
 * (`onAudienceResolvedCascadeConsumers`,
 * `onMembershipChangedRefanAudiences`).
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

// ── per-type details ──

export const flightDirectionSchema = z.enum(['inbound', 'outbound']);

const flightDetailsSchema = z.object({
  kind: z.literal('flight'),
  /**
   * Which leg of the trip this flight represents from the event's POV.
   * 'inbound' = passenger arriving to the event city.
   * 'outbound' = passenger departing the event city.
   * Nullable for legacy rows that pre-date this tag; new rows should
   * always set it.
   */
  direction: flightDirectionSchema.nullable(),
  airline: z.string().nullable(),
  flightNumber: z.string().nullable(),
  departureAirport: z.string().nullable(),
  departureAt: z.custom<TimestampLike>().nullable(),
  departureTerminal: z.string().nullable(),
  arrivalAirport: z.string().nullable(),
  arrivalAt: z.custom<TimestampLike>().nullable(),
  arrivalTerminal: z.string().nullable(),
  confirmationCode: z.string().nullable(),
  notes: z.string().nullable(),
});

const lodgingDetailsSchema = z.object({
  kind: z.literal('lodging'),
  checkInAt: z.custom<TimestampLike>().nullable(),
  checkOutAt: z.custom<TimestampLike>().nullable(),
  confirmationCode: z.string().nullable(),
  notes: z.string().nullable(),
});

export const transportModeSchema = z.enum([
  'shuttle',
  'car',
  'rideshare',
  'taxi',
  'train',
  'ferry',
  'walk',
  'custom',
]);

export const transportSpendLimitSchema = z.object({
  amount: z.number().nonnegative(),
  /** ISO 4217 currency code (e.g. "USD", "EUR"). */
  currency: z.string().min(3).max(3),
});

export const transportValidityWindowSchema = z.object({
  from: z.custom<TimestampLike>(),
  to: z.custom<TimestampLike>(),
});

const transportDetailsSchema = z.object({
  kind: z.literal('transport'),
  mode: transportModeSchema,
  /**
   * Scheduled pickup time. Null for open-ended voucher-style transport
   * (e.g. an Uber promo code valid throughout the event with no specific
   * ride scheduled).
   */
  pickupAt: z.custom<TimestampLike>().nullable(),
  /** Rideshare voucher code, ride-confirmation, etc. */
  code: z.string().nullable(),
  /** Optional claim/redemption URL for rideshare vouchers. */
  voucherUrl: z.string().url().nullable().optional(),
  /**
   * Optional per-ride or aggregate cap on the voucher's value. Null when
   * uncapped or not applicable.
   */
  spendLimit: transportSpendLimitSchema.nullable(),
  /**
   * Optional validity window for open-ended vouchers (e.g. valid Mon–Wed).
   * Null when the voucher is always-on for the event duration or when this
   * is a scheduled ride.
   */
  validityWindow: transportValidityWindowSchema.nullable(),
  notes: z.string().nullable(),
});

const otherDetailsSchema = z.object({
  kind: z.literal('other'),
  title: z.string(),
  description: z.string().nullable(),
  time: z.custom<TimestampLike>().nullable(),
  notes: z.string().nullable(),
});

export const travelItemDetailsSchema = z.discriminatedUnion('kind', [
  flightDetailsSchema,
  lodgingDetailsSchema,
  transportDetailsSchema,
  otherDetailsSchema,
]);

export type TravelItemKind = z.infer<typeof travelItemDetailsSchema>['kind'];

// ── reminder ──

export const travelReminderSchema = z.object({
  enabled: z.boolean(),
  /** Lead time in minutes before the relevant detail timestamp. Required when enabled. */
  leadMinutes: z.number().int().nonnegative(),
  /** Free-text body the organizer authored. No templating. */
  body: z.string(),
});

// ── document ──

export const travelItemDocumentSchema = z.object({
  eventId: z.string(),
  details: travelItemDetailsSchema,
  /**
   * Named pickup / dropoff slots referencing `eventLocations`. Replaces
   * the former indexed `locationIds[]` array — kind-specific UI maps
   * pickup/dropoff onto detail-screen labels:
   *
   *   - flight:    both null (airports are not event-map locations)
   *   - lodging:   pickup null, dropoff = hotel locationId
   *   - transport: pickup = pickup locationId, dropoff = dropoff locationId
   *   - other:     pickup null, dropoff = the single location
   *
   * Mobile renders pins by iterating both slots — order is not significant
   * for pin display, only for kind-specific labelling.
   */
  pickupLocationId: z.string().nullable(),
  dropoffLocationId: z.string().nullable(),
  /**
   * The Audience whose members can read this travel item. Resolved via
   * the cascading-fanout trigger to `targetedPersonIds[]` below.
   */
  audienceId: z.string(),
  /**
   * Optional UI affordance — surfaces "for: <session>" badge on the travel
   * item card. Distinct from `audienceId`: audience drives delivery, this
   * just drives display. Set to a session ID or null.
   */
  sessionLink: z.string().nullable(),
  /** Optional reminder. null when no reminder configured. */
  reminder: travelReminderSchema.nullable(),
  /**
   * CF-managed idempotency guard. The processScheduledTravelReminders cron
   * sets this once when the reminder fires, so subsequent ticks skip the
   * row even if the detail timestamp keeps moving forward.
   */
  reminderSentAt: z.custom<TimestampLike>().nullable(),
  /**
   * CF-managed personId cache copied directly from the linked audience's
   * `resolvedPersonIds[]` by `onTravelItemWritten` and re-fanned by the
   * cascading triggers when membership changes (audience cascade via
   * `onAudienceResolvedCascadeConsumers`, eventMemberships via
   * `onMembershipChangedRefanAudiences` → audience cascade chain). The
   * read rule is `request.auth.token.personId in targetedPersonIds`
   * (ADR-137). Client writes blocked by `affectedKeys` guard in
   * firestore.rules.
   */
  targetedPersonIds: z.array(z.string()),
  createdByUserId: z.string(),
});
